'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { Heading } from '../ui/heading';
import { ProductVariants } from '@eliadmin/core/src/components/product/detail/variants/product-variants';
import { ProductDetailPrice } from '@eliadmin/core/src/components/product/detail/product-detail-price';
import { ProductQuantity } from '@eliadmin/core/src/components/product/detail/product-quantity';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import { ButtonIcon } from '../ui/button-icon';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { ProductBadge } from './detail/product-badge';
import { Paragraph } from '../ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function WeekProductInfo({ product, label }: { product: Product; label: string }) {
  const { t } = useTranslations();
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const desiredObject = (product.variants?.length ?? 0) > 0 ? selectedVariant : product;

  const showAvailability = !!desiredObject?.availability;

  return (
    <div className="flex w-full flex-col items-start justify-between gap-6 md:max-w-[648px]">
      <Heading title={label} tag="h5" className={{ title: 'text-[#666666]' }} />
      <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-6 lg:gap-8">
        <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4">
          <div className="flex items-start justify-start gap-2">
            {showAvailability && (
              <ProductBadge
                text={product.availability?.name || ''}
                background={product.availability?.color || '#E0E0E0'}
                color={getContrastYIQ('#000000')}
              />
            )}
            {product.tags?.map(tag => (
              <ProductBadge
                key={tag.name}
                text={tag.name}
                background={tag.color ?? '#000000'}
                color={getContrastYIQ(tag.color ?? '#000000')}
              />
            ))}
          </div>
          <Heading
            title={selectedVariant?.name ?? product?.name}
            tag="h4"
            className={{ title: 'line-clamp-2' }}
          />
          <div className="flex w-full items-center justify-start gap-4">
            <Paragraph color="muted-foreground" size="lg">
              {t('text228')} {desiredObject?.code}
            </Paragraph>
          </div>
        </div>
        <ProductVariants />
        <ProductDetailPrice />
        <ProductQuantity />
      </div>
      <Link
        prefetchOnHover
        href={ROUTES.PRODUCT({ slug: product.slug })}
        className={cn(
          'h5',
          'flex items-center justify-start gap-2 font-semibold tracking-tight text-primary',
        )}
      >
        Celý detail produktu
        <ButtonIcon alt="Celý detail produktu" url="/images/icons/up-right-arrow.svg" />
      </Link>
    </div>
  );
}
